import { createGlsPostProcessor } from "@greco-it/i18n-editor";
import i18n from "i18next";
import Backend from "i18next-http-backend";
import { APP_ID } from "library/constants";
import { EAppSettings } from "library/types";
import { initReactI18next } from "react-i18next";
import { getUserAppSettings } from "state/queries/useGetUserAppSettings";
import { buildYupLocale } from "validation";
import { bundleSources, createBundleLoader, LOCAL_BUNDLES } from "./i18nBundleLoader";
import { urlFormat } from "./library/utils";

const defaultLanguage = "en-GB";

// Normalize legacy/malformed locale codes stored in the backend to valid BCP 47 tags.
// e.g. "sr-RS-Latn" was stored by the Excel import handler with wrong subtag order.
const legacyLocaleMap: Record<string, string> = {
  "sr-RS-Latn": "sr-Latn-RS",
};

const normalizeLocale = (locale: string): string =>
  legacyLocaleMap[locale] ?? locale;

/**
 * Where `{{lng}}.json` is read from, in order: the bundle Glossa publishes for project `hbh`
 * (`REACT_APP_I18N_BASE_URL`, e.g. https://glsdwesa.blob.core.windows.net/locales/hbh), the legacy
 * Phrase blob (`REACT_APP_PHRASE_BASE_URL`), then the copy in `public/locales`. Unset variables
 * are skipped; locally only `public/locales` remains.
 */
const bundleBaseUrls = bundleSources(
  urlFormat(process.env.REACT_APP_I18N_BASE_URL || ""),
  urlFormat(process.env.REACT_APP_PHRASE_BASE_URL || ""),
  LOCAL_BUNDLES
);

/**
 * Marks every rendered string while the Glossa in-context editor is on (see GlsEditor); one
 * comparison per t() otherwise. The provider must get this same instance.
 */
export const glsPostProcessor = createGlsPostProcessor();

const languageDetector = {
  type: "languageDetector" as any, // Exact type is 'backend' | 'logger' | 'languageDetector' | 'postProcessor' | 'i18nFormat' | '3rdParty'
  async: true, // flags below detection to be async
  detect: async (callback: Function) => {
    const userAppSettings = await getUserAppSettings({
      appId: APP_ID,
    });
    const lng = userAppSettings.find(
      (setting) => setting.userAppSettingName === EAppSettings.language
    );

    return callback(normalizeLocale(lng?.userAppSettingValue || defaultLanguage));
  },
  init: () => {},
  cacheUserLanguage: () => {},
};

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .use(Backend)
  .use(languageDetector)
  .use(glsPostProcessor)
  .init(
    {
      keySeparator: false,
      nsSeparator: false,
      interpolation: {
        escapeValue: false,
      },
      load: "currentOnly",
      backend: {
        loadPath: `${bundleBaseUrls[0]}/{{lng}}.json`,
        request: createBundleLoader(bundleBaseUrls),
      },
      // The editor loads the draft through addResourceBundle; without this nothing re-renders.
      react: { bindI18nStore: "added removed" },
      fallbackLng: (code: string) => {
        if (!code || code === "en-US") return ["en-GB", "de-AT"];

        if (code === "de-DE") return ["de-AT", "en-GB"];
        if (code === "de-CH") return ["de-AT", "en-GB"];
        if (code === "de-AT") return ["de-AT", "en-GB"];

        return ["en-GB", "de-AT"];
      },
      saveMissing: true,

      missingKeyHandler: (lngs, _, key, fallbackValue) => {
        // Check if the key is available in 'en-GB', and if so, return its value

        if (lngs[1] === "en-GB") {
          return i18n.t(key, { lng: "en-GB" });
        }

        // If not found in 'en-GB', return the fallback value
        console.warn(`Missing translation key: ${key}`);
        return fallbackValue;
      },
      supportedLngs: [
        "de-AT",
        "bs-BA",
        "bg-BG",
        "cs-CZ",
        "et-EE",
        "hr-HR",
        "hu-HU",
        "lt-LT",
        "pl-PL",
        "ro-RO",
        "ru-RU",
        "sr-RS",
        "sr-Latn-RS",
        "sk-SK",
        "sl-SI",
        "tr-TR",
        "uk-UA",
        "en-GB",
      ],
      postProcess: ["glsMarker"],
    },
    buildYupLocale
  );

export default i18n;
(window as any).i18n = i18n;
